/**
 * @file DeliveryAgreementSentPanel.tsx
 * @description Состояние «письмо ушло, ждём клиента» в блоке согласования
 * доставки (phase19.24 шаг 5.1, §3).
 *
 * Ход клиента — значит форму условий убираем: менеджеру нужны срок, ссылка
 * (её диктуют по телефону) и три действия — «Изменить исход», «Отправить
 * повторно» и меню «⋯». Просрочка объясняется словами и красным: по таймеру
 * заявка отменится, и об этом нельзя узнать постфактум.
 */

import { Copy, Link2, Pencil, XCircle } from "lucide-react";

import { formatShopDateTime } from "../../lib/shop-time";
import type { DeliveryAgreementDto } from "../../lib/api-delivery-agreement";
import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import { RowActionsMenu } from "../ui/RowActionsMenu";
import { useNotify } from "../ui/NotifyProvider";

interface Props {
  agreement: DeliveryAgreementDto;
  /** Срок вышел: клиент молчит дольше 72 часов. */
  overdue: boolean;
  disabled?: boolean;
  onEdit: () => void;
  onResend: () => void;
  /** «Отменить заявку»: снять предложение и погасить ссылку. */
  onCancelRequest: () => void;
}

export function DeliveryAgreementSentPanel({
  agreement,
  overdue,
  disabled,
  onEdit,
  onResend,
  onCancelRequest,
}: Props) {
  const notify = useNotify();
  const link = agreement.confirmUrl;

  async function copyLink(): Promise<void> {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      notify.success("Ссылка скопирована — можно отправить клиенту");
    } catch {
      // Буфер недоступен (http без TLS, запрет браузера) — ссылка всё равно
      // видна текстом рядом, менеджер продиктует её по телефону.
      notify.warning("Скопировать не вышло — ссылка показана ниже, её можно выделить");
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {overdue ? (
        <NoticeStrip tone="error">
          Клиент не подтвердил за 72 часа. Позвоните или отправьте заново — по
          таймеру заказ будет отменён.
        </NoticeStrip>
      ) : (
        <NoticeStrip>
          Ждём клиента
          {agreement.expiresAt ? ` до ${formatShopDateTime(agreement.expiresAt)}` : ""}.
          Подтверждение приходит по ссылке из письма.
          {agreement.agreementChannel === "PHONE"
            ? " Заявка согласуется по телефону — письмо не отправлялось, ссылку диктуем."
            : ""}
        </NoticeStrip>
      )}

      {link ? (
        <div className="flex flex-col gap-1.5 rounded-xl bg-surface-low px-4 py-3">
          <span className="flex items-center gap-2 text-sm text-on-surface-variant">
            <Link2 className="size-4 shrink-0" aria-hidden />
            Ссылка подтверждения — её можно продиктовать клиенту
          </span>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-sm text-primary hover:underline"
          >
            {link}
          </a>
        </div>
      ) : null}

      <div className="flex flex-col gap-2 lg:flex-row lg:justify-end">
        <Button
          variant="outline"
          className="min-h-14 lg:min-h-12"
          disabled={disabled}
          onClick={onEdit}
        >
          <Pencil className="mr-1 size-4" />
          Изменить исход
        </Button>
        <RowActionsMenu
          actions={[
            {
              key: "copy",
              label: "Скопировать ссылку",
              icon: Copy,
              disabled: !link,
              run: () => void copyLink(),
            },
            // Деструктивное действие — последним (правило A6).
            {
              key: "cancel",
              label: "Отменить заявку",
              icon: XCircle,
              danger: true,
              run: onCancelRequest,
            },
          ]}
          title="Действия с заявкой"
          ariaLabel="Другие действия с заявкой"
          disabled={disabled}
        />
        <Button className="min-h-14 lg:min-h-12" disabled={disabled} onClick={onResend}>
          Отправить повторно
        </Button>
      </div>
    </div>
  );
}
